import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    Box,
    Button,
    ButtonProps,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitleProps,
    FormControlLabel,
    IconButtonProps,
    Dialog as MuiDialog,
    DialogTitle as MuiDialogTitle,
    IconButton as MuiIconButton,
    styled,
    Theme,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";

import { mutationDeleteCategory } from "src/api/category";
import { mutationDeleteElement } from "src/api/element";
import { mutationDeleteMap } from "src/api/map";
import { mutationDeleteUser } from "src/api/user";
import { LanguageContext } from "src/contexts/language";
import { MapContext } from "src/contexts/map";
import { SettingContext } from "src/contexts/settings";
import { Enum_Entity } from "src/types";
import { Type_Element_Data } from "src/types/element";

type Type_Props_Delete_Dialog = {
    closeDeleteDialog: () => void;
    closeDetailsDialog?: () => void;
    entity?: string;
};

const DialogTitle = styled(MuiDialogTitle)<DialogTitleProps>(() => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
}));

const IconButton = styled(MuiIconButton)<IconButtonProps>(() => ({
    marginLeft: "auto",
}));

const DeleteButton = styled(Button)<ButtonProps>(({ theme }) => ({
    marginRight: theme.spacing(4),
    marginBottom: theme.spacing(1),
}));

const Dialog = styled(MuiDialog)<DialogProps>(({ theme }) => ({
    "& .MuiDialog-paper": {
        [theme.breakpoints.down("md")]: {
            position: "absolute",
            bottom: 0,
            margin: 0,
            width: "100%",
        },
    },
}));

const DeleteDialog = ({
    closeDeleteDialog,
    closeDetailsDialog,
    entity = Enum_Entity.ELEMENT,
}: Type_Props_Delete_Dialog) => {
    const { newElement, categories, selectedMap } = useContext(MapContext);
    const { user, map, category } = useContext(SettingContext);

    const { language } = useContext(LanguageContext);
    const { t } = useTranslation();
    const [isChecked, setIsChecked] = useState(false);
    const queryClient = useQueryClient();
    const isMobile = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down("md"),
    );

    const element = newElement as Type_Element_Data;
    const token = sessionStorage.getItem("authToken");
    const { mutateAsync: mutateDeleteElement } = mutationDeleteElement() || {};
    const { mutateAsync: mutateDeleteUser } =
        mutationDeleteUser(token || "") || {};
    const { mutateAsync: mutateDeleteMap } = mutationDeleteMap() || {};
    const { mutateAsync: mutateDeleteCategory } =
        mutationDeleteCategory() || {};

    const elementCategory = element
        ? categories.find((category) => category.id === element.category_id)
        : null;

    const entityVariables = [
        {
            entity: Enum_Entity.ELEMENT,
            name:
                elementCategory &&
                `${elementCategory.title[language].toUpperCase()} : ${element.title}`,
            submitFunc: () => mutateDeleteElement(element.id),
            queryKey: ["elements", selectedMap?.id],
        },
        {
            entity: Enum_Entity.USER,
            name: user && user.email,
            submitFunc: () => mutateDeleteUser(user!.id),
            queryKey: ["users", token],
        },
        {
            entity: Enum_Entity.MAP,
            name: map && map.label,
            submitFunc: () => mutateDeleteMap(map!.id),
            queryKey: ["maps"],
        },
        {
            entity: Enum_Entity.CATEGORY,
            name: category && category.title,
            submitFunc: () => mutateDeleteCategory(category!.id),
            queryKey: ["categories"],
        },
    ];

    const [selectedEntity, setSelectedEntity] = useState(entityVariables[0]);

    useEffect(() => {
        const newEntity = entityVariables.find(
            (varItem) => varItem.entity === entity,
        );
        if (newEntity) setSelectedEntity(newEntity);
    }, []);

    return (
        <Dialog open onClose={closeDeleteDialog}>
            <DialogTitle>
                <IconButton onClick={closeDeleteDialog} size="small">
                    <FontAwesomeIcon icon={faTimes} />
                </IconButton>
                <Typography>
                    {isMobile ? (
                        <strong>
                            {t("deleteDialog.info", {
                                name: selectedEntity.name,
                            })}
                        </strong>
                    ) : (
                        <>
                            {t("deleteDialog.info", {
                                name: selectedEntity.name,
                            })}{" "}
                            {t("deleteDialog.sub_info")}
                        </>
                    )}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isChecked}
                                onChange={(e) => setIsChecked(e.target.checked)}
                            />
                        }
                        label={
                            isMobile ? (
                                t("deleteDialog.confirm", {
                                    name: selectedEntity.name,
                                })
                            ) : (
                                <strong>
                                    {t("deleteDialog.confirm", {
                                        name: selectedEntity.name,
                                    })}
                                </strong>
                            )
                        }
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <DeleteButton
                    variant="contained"
                    color="error"
                    startIcon={<FontAwesomeIcon icon={faCheck} />}
                    onClick={async () => {
                        await selectedEntity.submitFunc();
                        closeDeleteDialog();
                        if (closeDetailsDialog) closeDetailsDialog();
                        queryClient.invalidateQueries(selectedEntity.queryKey);
                    }}
                    disabled={!isChecked}
                >
                    {t("delete")}
                </DeleteButton>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteDialog;
